import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { useLocation } from "react-router-dom";
import { Button } from "../components/ui/button";
import { CheckCircle, XCircle, Clock, Calendar, Eye } from "lucide-react";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../components/ui/dialog";
import { Label } from "../components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { Spinner } from "../components/ui/shadcn-io/spinner";
import { DataTable } from "../components/dataTable";
import { CompOff, createAdminCompOffColumns } from "../lib/compOffTableColumns";

interface CompOffStats {
  pending: number;
  approved: number;
  rejected: number;
  totalCredits: number;
}

export default function AdminCompOff() {
  const location = useLocation();
  const [compOffs, setCompOffs] = useState<CompOff[]>([]);
  const [stats, setStats] = useState<CompOffStats | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState("");
  const [successMessage, setSuccessMessage] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");

  const [selectedCompOff, setSelectedCompOff] = useState<CompOff | null>(null);
  const [isViewDialogOpen, setIsViewDialogOpen] = useState(false);
  const [isActionDialogOpen, setIsActionDialogOpen] = useState(false);
  const [actionType, setActionType] = useState<"approve" | "reject" | null>(
    null
  );
  const [isActing, setIsActing] = useState(false);

  useEffect(() => {
    if (location.state?.message) {
      setSuccessMessage(location.state.message);
      window.history.replaceState(null, "");

      setTimeout(() => {
        setSuccessMessage("");
      }, 5000);
    }
  }, [location.state]);

  useEffect(() => {
    fetchCompOffs();
  }, [statusFilter]);

  const fetchCompOffs = async () => {
    try {
      setIsLoading(true);
      setError("");

      const token = localStorage.getItem("authToken");
      const response = await axios.get(
        `${process.env.REACT_APP_BACKEND_URL}api/compoff/all?status=${statusFilter}`,
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );

      const normalized = (response.data || []).map((item: any) => ({
        ...item,
        id: item._id,
        status: item.status || (item.isExhausted ? "Exhausted" : "Pending"),
        requestedDate:
          item.requestedDate || item.appliedDate || item.createdAt || item.updatedAt,
      }));

      setCompOffs(normalized);
      calculateStats(normalized);
    } catch (err: any) {
      console.error("Error fetching comp-off requests:", err);
      setError(
        err.response?.data?.message || "Failed to fetch comp-off requests"
      );
    } finally {
      setIsLoading(false);
    }
  };

  const calculateStats = (data: CompOff[]) => {
    const pending = data.filter((c) => c.status === "Pending").length;
    const approved = data.filter((c) => c.status === "Approved");
    const rejected = data.filter((c) => c.status === "Rejected").length;
    const totalCredits = approved.reduce(
      (sum, c) => sum + (c.creditDays || 0),
      0
    );
    setStats({
      pending,
      approved: approved.length,
      rejected,
      totalCredits,
    });
  };

  const handleApproveClick = (compOff: CompOff) => {
    setSelectedCompOff(compOff);
    setActionType("approve");
    setIsActionDialogOpen(true);
  };

  const handleRejectClick = (compOff: CompOff) => {
    setSelectedCompOff(compOff);
    setActionType("reject");
    setIsActionDialogOpen(true);
  };

  const handleViewClick = (compOff: CompOff) => {
    setSelectedCompOff(compOff);
    setIsViewDialogOpen(true);
  };

  const handleConfirmAction = async () => {
    if (!selectedCompOff || !actionType) return;

    try {
      setIsActing(true);
      const token = localStorage.getItem("authToken");
      await axios.put(
        `${process.env.REACT_APP_BACKEND_URL}api/compoff/${selectedCompOff._id}/status`,
        { status: actionType === "approve" ? "Approved" : "Rejected" },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      setSuccessMessage(
        `Comp-off ${
          actionType === "approve" ? "approved" : "rejected"
        } successfully!`
      );
      setIsActionDialogOpen(false);
      setSelectedCompOff(null);
      setActionType(null);
      fetchCompOffs();
    } catch (err: any) {
      console.error("Error updating comp-off status:", err);
      setError(
        err.response?.data?.message || "Failed to update comp-off status"
      );
    } finally {
      setIsActing(false);
    }
  };

  const columns = useMemo(
    () =>
      createAdminCompOffColumns(
        handleApproveClick,
        handleRejectClick,
        handleViewClick
      ),
    []
  );

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="flex flex-col items-center gap-2">
          <Spinner variant="ellipsis" size={40} />
          <p className="text-sm text-muted-foreground">
            Loading comp-off requests...
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="container mx-auto px-6 py-8">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-3xl font-bold">Comp-Off Requests</h1>
          <p className="text-muted-foreground mt-1">
            Manage compensatory off approvals
          </p>
        </div>
      </div>

      {successMessage && (
        <div className="mb-6 p-4 bg-green-50 border border-green-200 rounded-lg flex items-start gap-3">
          <CheckCircle className="h-5 w-5 text-green-600 mt-0.5" />
          <div className="flex-1">
            <p className="text-green-800 font-medium">{successMessage}</p>
          </div>
          <button
            onClick={() => setSuccessMessage("")}
            className="text-green-600 hover:text-green-800"
          >
            ×
          </button>
        </div>
      )}

      {error && (
        <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg flex items-start gap-3">
          <XCircle className="h-5 w-5 text-red-600 mt-0.5" />
          <div className="flex-1">
            <p className="text-red-800 font-medium">{error}</p>
          </div>
          <button
            onClick={() => setError("")}
            className="text-red-600 hover:text-red-800"
          >
            ×
          </button>
        </div>
      )}

      {stats && (
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-6">
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium text-muted-foreground">
                Pending Requests
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex items-center gap-2">
                <Clock className="h-5 w-5 text-yellow-600" />
                <span className="text-2xl font-bold">{stats.pending}</span>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium text-muted-foreground">
                Approved
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex items-center gap-2">
                <CheckCircle className="h-5 w-5 text-green-600" />
                <span className="text-2xl font-bold">{stats.approved}</span>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium text-muted-foreground">
                Rejected
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex items-center gap-2">
                <XCircle className="h-5 w-5 text-red-600" />
                <span className="text-2xl font-bold">{stats.rejected}</span>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium text-muted-foreground">
                Approved Credits
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex items-center gap-2">
                <Calendar className="h-5 w-5 text-blue-600" />
                <span className="text-2xl font-bold">
                  {stats.totalCredits} day(s)
                </span>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      <div className="mb-6">
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="w-[200px]">
            <SelectValue placeholder="Filter by status" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Requests</SelectItem>
            <SelectItem value="Pending">Pending</SelectItem>
            <SelectItem value="Approved">Approved</SelectItem>
            <SelectItem value="Rejected">Rejected</SelectItem>
          </SelectContent>
        </Select>
      </div>

      <DataTable columns={columns} data={compOffs} />

      <Dialog open={isViewDialogOpen} onOpenChange={setIsViewDialogOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Comp-Off Request Details</DialogTitle>
            <DialogDescription>
              Complete information about the comp-off request
            </DialogDescription>
          </DialogHeader>

          {selectedCompOff && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-muted-foreground">Employee Name</Label>
                  <p className="font-medium mt-1">
                    {selectedCompOff.employee?.fullName ||
                      selectedCompOff.employeeName ||
                      "N/A"}
                  </p>
                </div>
                <div>
                  <Label className="text-muted-foreground">Email</Label>
                  <p className="font-medium mt-1">
                    {selectedCompOff.employee?.email ||
                      selectedCompOff.employeeEmail ||
                      "N/A"}
                  </p>
                </div>
                <div>
                  <Label className="text-muted-foreground">Worked On</Label>
                  <p className="font-medium mt-1">
                    {new Date(selectedCompOff.workDate).toLocaleDateString()}
                  </p>
                </div>
                <div>
                  <Label className="text-muted-foreground">Credit Days</Label>
                  <p className="font-medium mt-1">
                    {selectedCompOff.creditDays}
                  </p>
                </div>
                <div>
                  <Label className="text-muted-foreground">Hours Worked</Label>
                  <p className="font-medium mt-1">
                    {selectedCompOff.hoursWorked} hrs
                  </p>
                </div>
                <div>
                  <Label className="text-muted-foreground">Status</Label>
                  <div className="mt-1">
                    <Badge
                      className={
                        selectedCompOff.status === "Approved"
                          ? "bg-green-100 text-green-800"
                          : selectedCompOff.status === "Rejected"
                          ? "bg-red-100 text-red-800"
                          : "bg-yellow-100 text-yellow-800"
                      }
                    >
                      {selectedCompOff.status}
                    </Badge>
                  </div>
                </div>
                {selectedCompOff.expiryDate && (
                  <div>
                    <Label className="text-muted-foreground">Expiry</Label>
                    <p className="font-medium mt-1">
                      {new Date(
                        selectedCompOff.expiryDate
                      ).toLocaleDateString()}
                    </p>
                  </div>
                )}
              </div>

              <div>
                <Label className="text-muted-foreground">Reason</Label>
                <p className="mt-1 p-3 bg-muted rounded-md">
                  {selectedCompOff.reason}
                </p>
              </div>
            </div>
          )}

          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setIsViewDialogOpen(false)}
            >
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={isActionDialogOpen} onOpenChange={setIsActionDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {actionType === "approve"
                ? "Approve Comp-Off"
                : "Reject Comp-Off"}
            </DialogTitle>
            <DialogDescription>
              {actionType === "approve"
                ? "Approve this comp-off request?"
                : "Reject this comp-off request?"}
            </DialogDescription>
          </DialogHeader>

          {selectedCompOff && (
            <div className="p-3 bg-muted rounded-md space-y-2">
              <p>
                <span className="font-medium">Employee:</span>{" "}
                {selectedCompOff.employee?.fullName ||
                  selectedCompOff.employeeName ||
                  "N/A"}
              </p>
              <p>
                <span className="font-medium">Worked On:</span>{" "}
                {new Date(selectedCompOff.workDate).toLocaleDateString()}
              </p>
              <p>
                <span className="font-medium">Credit Days:</span>{" "}
                {selectedCompOff.creditDays}
              </p>
            </div>
          )}

          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setIsActionDialogOpen(false)}
              disabled={isActing}
            >
              Cancel
            </Button>
            <Button
              onClick={handleConfirmAction}
              disabled={isActing}
              className={
                actionType === "approve"
                  ? "bg-green-600 hover:bg-green-700"
                  : "bg-red-600 hover:bg-red-700"
              }
            >
              {isActing ? (
                <>
                  <Spinner variant="ellipsis" size={16} className="mr-2" />
                  Processing...
                </>
              ) : actionType === "approve" ? (
                <>
                  <CheckCircle className="mr-2 h-4 w-4" />
                  Approve
                </>
              ) : (
                <>
                  <XCircle className="mr-2 h-4 w-4" />
                  Reject
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
